import { getErrorMessage } from '../../utils/serviceUtils';
import { GameEvent, EventWithSequence } from '../../types/game';
import {
  OnlineClientCommand,
  OnlineClientContext,
  OnlineLobbyStateSnapshot,
  OnlineServerMessage,
} from '../../types/transport';
import { normalizeEventForRpc } from '../../types/eventContracts';
import { classifyGameRestartedPayload } from '../../domain/gameRestarted';
import { RapidSettings } from '../../domain/rapid';
import { isVerboseLoggingEnabled, logger } from '../../utils/logger';
import { recordDebugTrace } from '../../context/debug/debugLog';
import { createOnlineTransport } from './createOnlineTransport';
import { getOnlineTransportSession } from './onlineTransportRuntime';
import { OnlineSnapshot } from '../../types/snapshots';
import { getSessionPlayerId } from '../../utils/sessionPlayer';

export type AppendEventResult = {
  success: boolean;
  sequenceNumber?: number;
  acknowledgedEventId?: string;
  warning?: string;
  error?: string;
  errorCode?: string;
  errorDetails?: string | null;
  errorHint?: string | null;
  snapshot?: OnlineSnapshot | null;
};

type CommandResult = {
  success: boolean;
  eventId?: string | null;
  error?: string;
  errorCode?: string | null;
  errorDetails?: string | null;
  errorHint?: string | null;
};

type CommandDelivery = 'auto' | 'ephemeral';

interface OnlineGameHandle {
  appendEvent(event: GameEvent): Promise<AppendEventResult>;
  sendIntent(intent: OnlineClientCommand, options?: { delivery?: CommandDelivery }): Promise<CommandResult>;
  read: {
    snapshot(): Promise<OnlineSnapshot | null>;
    lobby(): Promise<OnlineLobbyStateSnapshot | null>;
    events(sinceSequence?: number): Promise<EventWithSequence[]>;
  };
}

export interface OnlineGameBoundary {
  forGame(gameId: string, actor?: Partial<OnlineClientContext>): OnlineGameHandle;
}

function readTimeoutMs(envKey: string, fallback: number): number {
  const fromMeta = typeof import.meta !== 'undefined'
    ? (import.meta.env as Record<string, unknown> | undefined)?.[envKey]
    : undefined;
  const fromProcess = typeof process !== 'undefined'
    ? process.env?.[envKey]
    : undefined;
  const raw = fromMeta ?? fromProcess;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return fallback;
  const normalized = Math.floor(parsed);
  return normalized >= 500 ? normalized : fallback;
}

function isTestRuntime(): boolean {
  const env = typeof process !== 'undefined' ? process.env : undefined;
  return env?.NODE_ENV === 'test' || env?.VITEST === 'true';
}

const OPEN_TIMEOUT_MS = readTimeoutMs('VITE_PARTYKIT_OPEN_TIMEOUT_MS', 3000);
const STATE_TIMEOUT_MS = readTimeoutMs('VITE_PARTYKIT_STATE_TIMEOUT_MS', 3000);
const SNAPSHOT_TIMEOUT_MS = readTimeoutMs(
  'VITE_PARTYKIT_SNAPSHOT_TIMEOUT_MS',
  isTestRuntime() ? 2000 : 9000,
);
const COMMAND_ACK_TIMEOUT_MS = readTimeoutMs('VITE_PARTYKIT_COMMAND_ACK_TIMEOUT_MS', 3500);
const SNAPSHOT_MAX_FETCH_ATTEMPTS = isTestRuntime() ? 4 : 8;
const RUNTIME_SESSION_READY_GRACE_MS = readTimeoutMs('VITE_PARTYKIT_RUNTIME_READY_GRACE_MS', 700);
const RECOVERABLE_RUNTIME_AUTH_ERRORS = new Set([
  'AUTH_CONTEXT_MISSING',
  'PLAYER_ID_MISMATCH',
  'PLAYER_NOT_ACTIVE',
  'HOST_REQUIRED',
]);
const TURN_INTENT_COMMANDS = new Set<OnlineClientCommand['type']>([
  'ROLL_INTENT',
  'KEEP_INTENT',
  'END_TURN_INTENT',
]);

type PartyKitCommandErrorSnapshot = {
  at: string;
  gameId: string;
  commandType: string;
  eventId: string | null;
  source: 'runtime' | 'ephemeral';
  code?: string | null;
  message?: string | null;
  serverTs?: string | null;
  turn?: number | null;
  activePlayerId?: string | null;
  reason?: string | null;
};

declare global {
  interface Window {
    __LAST_PARTYKIT_COMMAND_ERROR__?: PartyKitCommandErrorSnapshot;
  }
}

function getCommandType(command: OnlineClientCommand): string {
  return command.type;
}

function getExpectedAppliedEventId(command: OnlineClientCommand): string | null {
  if (command.type === 'ROLL_INTENT') return command.eventId;
  if (command.type === 'KEEP_INTENT') return command.eventId;
  if (command.type === 'END_TURN_INTENT') return command.eventId;
  if (command.type === 'REAL_SCORE') return command.eventId;
  if (command.type === 'REAL_PENALTY') return command.eventId;
  if (command.type === 'REAL_OVERTAKE') return command.eventId;
  if (command.type === 'GAME_CREATED') return command.eventId;
  if (command.type === 'ADD_BOT') return command.eventId;
  if (command.type === 'REMOVE_BOT') return command.eventId;
  if (command.type === 'UPSERT_PLAYER') return command.eventId;
  if (command.type === 'SET_READY') return command.eventId;
  if (command.type === 'FINALIZE_ORDER') return command.eventId;
  if (command.type === 'START_GAME') return command.eventId;
  if (command.type === 'SET_SETTINGS') return command.eventId;
  if (command.type === 'SET_DEV_BOT_BEHAVIOR') return null;
  if (command.type === 'HOST_TAKEOVER') return command.eventId;
  if (command.type === 'PLAYER_LEFT') return command.eventId;
  if (command.type === 'GAME_RESTARTED') return command.eventId;
  return null;
}

function getCommandEventId(command: OnlineClientCommand): string | null {
  return getExpectedAppliedEventId(command);
}

function inferSenderPlayerId(command: OnlineClientCommand): string | null {
  if (
    command.type === 'ROLL_INTENT'
    || command.type === 'KEEP_INTENT'
    || command.type === 'END_TURN_INTENT'
    || command.type === 'REAL_SCORE'
    || command.type === 'REAL_PENALTY'
    || command.type === 'SET_READY'
    || command.type === 'PLAYER_LEFT'
  ) {
    return command.playerId;
  }
  if (
    command.type === 'ADD_BOT'
    || command.type === 'REMOVE_BOT'
    || command.type === 'SET_SETTINGS'
    || command.type === 'SET_DEV_BOT_BEHAVIOR'
  ) {
    return command.hostId;
  }
  if (command.type === 'REAL_OVERTAKE') {
    return command.overtakerId;
  }
  return null;
}

function resolveClientContext(
  command: OnlineClientCommand,
  client?: Partial<OnlineClientContext>,
): OnlineClientContext {
  const fallbackPlayerId = inferSenderPlayerId(command) || getSessionPlayerId();
  return {
    playerId: client?.playerId ?? fallbackPlayerId ?? null,
    playerName: client?.playerName ?? null,
    role: client?.role ?? null,
  };
}

function setLastPartyKitCommandError(snapshot: PartyKitCommandErrorSnapshot) {
  if (typeof window === 'undefined') return;
  window.__LAST_PARTYKIT_COMMAND_ERROR__ = snapshot;
}

function getLastPartyKitCommandError(): PartyKitCommandErrorSnapshot | null {
  if (typeof window === 'undefined') return null;
  return window.__LAST_PARTYKIT_COMMAND_ERROR__ || null;
}

function recordTransportTelemetry(
  label: string,
  payload: Record<string, unknown>,
  tags: string[] = [],
) {
  if (isVerboseLoggingEnabled()) {
    logger.warn(label, payload);
  }
  recordDebugTrace(`transport:${label}`, payload, {
    source: 'onlineGameBoundary',
    tags: ['partykit', 'transport', ...tags],
  });
}

async function waitForSessionReady(sendStatusSubscription: (handler: (status: string) => void) => () => void): Promise<boolean> {
  return await new Promise<boolean>((resolve) => {
    let settled = false;
    let unsubscribe: () => void = () => {};
    const timeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      unsubscribe();
      resolve(false);
    }, OPEN_TIMEOUT_MS);

    unsubscribe = sendStatusSubscription((status: string) => {
      if (settled) return;
      if (status !== 'SUBSCRIBED') return;
      settled = true;
      clearTimeout(timeout);
      unsubscribe();
      resolve(true);
    });
  });
}

async function isRuntimeSessionReady(
  session: { onStatus?: (handler: (status: string) => void) => () => void },
): Promise<boolean> {
  const onStatus = session.onStatus;
  if (!onStatus) return true;
  return await new Promise<boolean>((resolve) => {
    let settled = false;
    let unsubscribe: () => void = () => {};
    const timeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      unsubscribe();
      resolve(false);
    }, RUNTIME_SESSION_READY_GRACE_MS);
    unsubscribe = onStatus.call(session, (status: string) => {
      if (settled) return;
      if (status !== 'SUBSCRIBED') return;
      settled = true;
      clearTimeout(timeout);
      unsubscribe();
      resolve(true);
    });
  });
}

async function waitForCommandAck(
  session: { onMessage?: (handler: (message: OnlineServerMessage) => void) => () => void },
  command: OnlineClientCommand,
  context: { gameId: string; source: 'runtime' | 'ephemeral' },
): Promise<boolean> {
  const expectedEventId = getExpectedAppliedEventId(command);
  if (!expectedEventId) {
    return true;
  }
  if (!session.onMessage) {
    return false;
  }

  return await new Promise<boolean>((resolve) => {
    let settled = false;
    let unsubscribe: () => void = () => {};

    const timeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      unsubscribe();
      const snapshot: PartyKitCommandErrorSnapshot = {
        at: new Date().toISOString(),
        gameId: context.gameId,
        commandType: getCommandType(command),
        eventId: expectedEventId,
        source: context.source,
        reason: 'ACK_TIMEOUT',
      };
      setLastPartyKitCommandError(snapshot);
      recordTransportTelemetry('PARTYKIT_COMMAND_ACK_TIMEOUT', snapshot, ['ack', 'timeout']);
      resolve(false);
    }, COMMAND_ACK_TIMEOUT_MS);

    unsubscribe = session.onMessage!((message: OnlineServerMessage) => {
      if (settled) return;
      if (message.type === 'ERROR') {
        settled = true;
        clearTimeout(timeout);
        unsubscribe();
        const snapshot: PartyKitCommandErrorSnapshot = {
          at: new Date().toISOString(),
          gameId: context.gameId,
          commandType: getCommandType(command),
          eventId: expectedEventId,
          source: context.source,
          code: message.code,
          message: message.message,
          serverTs: message.serverTs ?? null,
          turn: typeof message.turn === 'number' ? message.turn : null,
          activePlayerId: message.activePlayerId ?? null,
          reason: 'SERVER_ERROR',
        };
        setLastPartyKitCommandError(snapshot);
        recordTransportTelemetry('PARTYKIT_COMMAND_REJECTED', snapshot, ['ack', 'error']);
        resolve(false);
        return;
      }
      if (message.type !== 'EVENT_APPLIED') return;
      if (message.event.id !== expectedEventId) return;
      settled = true;
      clearTimeout(timeout);
      unsubscribe();
      resolve(true);
    });
  });
}

async function waitForLobbyStateFromSession(
  session: { onMessage: (handler: (message: OnlineServerMessage) => void) => () => void },
): Promise<OnlineLobbyStateSnapshot | null> {
  return new Promise<OnlineLobbyStateSnapshot | null>((resolve) => {
    let settled = false;
    let unsubscribe: () => void = () => {};
    const timeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      unsubscribe();
      resolve(null);
    }, STATE_TIMEOUT_MS);

    unsubscribe = session.onMessage((message: OnlineServerMessage) => {
      if (settled) return;
      if (message.type !== 'LOBBY_STATE') return;
      settled = true;
      clearTimeout(timeout);
      unsubscribe();
      resolve(message.state);
    });
  });
}

async function waitForStateSnapshotFromSession(
  session: { onMessage: (handler: (message: OnlineServerMessage) => void) => () => void },
): Promise<OnlineSnapshot | null> {
  return new Promise<OnlineSnapshot | null>((resolve) => {
    let settled = false;
    let unsubscribe: () => void = () => {};
    const timeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      unsubscribe();
      resolve(null);
    }, STATE_TIMEOUT_MS);

    unsubscribe = session.onMessage((message: OnlineServerMessage) => {
      if (settled) return;
      if (message.type !== 'SNAPSHOT') return;
      if (!('scores' in message.snapshot) || typeof message.snapshot.gameId !== 'string') return;
      settled = true;
      clearTimeout(timeout);
      unsubscribe();
      resolve(message.snapshot);
    });
  });
}

async function waitForSnapshotEventsFromSession(
  session: { onMessage: (handler: (message: OnlineServerMessage) => void) => () => void },
  sinceSequence: number,
): Promise<{ events: GameEvent[]; completed: boolean; timedOut: boolean }> {
  return new Promise((resolve) => {
    const events: GameEvent[] = [];
    let settled = false;
    let unsubscribe: () => void = () => {};
    const hardTimeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      unsubscribe();
      resolve({ events, completed: false, timedOut: true });
    }, SNAPSHOT_TIMEOUT_MS);

    unsubscribe = session.onMessage((message: OnlineServerMessage) => {
      if (settled) return;
      if (message.type === 'ERROR') {
        settled = true;
        clearTimeout(hardTimeout);
        unsubscribe();
        resolve({ events, completed: false, timedOut: false });
        return;
      }
      if (message.type === 'SNAPSHOT_END') {
        if (message.sinceSequence !== sinceSequence) return;
        settled = true;
        clearTimeout(hardTimeout);
        unsubscribe();
        resolve({ events, completed: true, timedOut: false });
        return;
      }
      if (message.type !== 'EVENT_APPLIED') return;
      const sequence = typeof message.sequenceNumber === 'number'
        ? message.sequenceNumber
        : typeof (message.event as { sequenceNumber?: unknown }).sequenceNumber === 'number'
          ? (message.event as { sequenceNumber: number }).sequenceNumber
          : null;
      if (sequence != null && sequence <= sinceSequence) return;
      events.push(message.event);
    });
  });
}

function resolveClientFromEvent(event: GameEvent): Partial<OnlineClientContext> {
  const payload = event.payload as unknown as Record<string, unknown>;
  const useSessionActor = event.type === 'ADD_PLAYER' || event.type === 'VIEWER_JOINED';
  const playerIdFromPayload = useSessionActor
    ? null
    : (typeof payload?.playerId === 'string'
      ? payload.playerId
      : typeof payload?.viewerId === 'string'
        ? payload.viewerId
        : null);
  const playerId = playerIdFromPayload || getSessionPlayerId();
  const playerName = useSessionActor ? null : (typeof payload?.name === 'string' ? payload.name : null);
  return { playerId, playerName, role: null };
}

function parseKeptMask(input: unknown): boolean[] | null {
  if (!Array.isArray(input) || input.length !== 5) return null;
  return input.map((value) => value === true);
}

function buildCommandFromEvent(event: GameEvent, currentRoomId: string): OnlineClientCommand | null {
  const payload = event.payload as unknown as Record<string, unknown>;
  if (event.type === 'SCORE') {
    const playerId = typeof payload.playerId === 'string' ? payload.playerId : null;
    if (!playerId) return null;
    const delta = Number(payload.delta);
    if (!Number.isFinite(delta)) return null;
    const rawResultingScore = payload.resultingScore;
    const resultingScore = Number(rawResultingScore);
    return { type: 'REAL_SCORE', eventId: event.id, turn: event.turn, playerId, delta, ...(Number.isFinite(resultingScore) ? { resultingScore } : {}) };
  }
  if (event.type === 'PENALTY') {
    const playerId = typeof payload.playerId === 'string' ? payload.playerId : null;
    const reason = typeof payload.reason === 'string' && payload.reason.length > 0 ? payload.reason : null;
    if (!playerId || !reason) return null;
    const delta = Number(payload.delta);
    if (!Number.isFinite(delta)) return null;
    const rawResultingScore = payload.resultingScore;
    const resultingScore = Number(rawResultingScore);
    return { type: 'REAL_PENALTY', eventId: event.id, turn: event.turn, playerId, delta, reason, ...(Number.isFinite(resultingScore) ? { resultingScore } : {}) };
  }
  if (event.type === 'OVERTAKE') {
    const overtakerId = typeof payload.overtakerId === 'string' ? payload.overtakerId : null;
    const bonus = Number(payload.bonus);
    const penalties = Array.isArray(payload.penalties)
      ? payload.penalties.map((entry) => {
          if (!entry || typeof entry !== 'object') return null;
          const playerId = typeof (entry as Record<string, unknown>).playerId === 'string' ? (entry as Record<string, unknown>).playerId as string : null;
          const delta = Number((entry as Record<string, unknown>).delta);
          if (!playerId || !Number.isFinite(delta)) return null;
          return { playerId, delta };
        }).filter((entry): entry is { playerId: string; delta: number } => Boolean(entry))
      : [];
    if (!overtakerId || !Number.isFinite(bonus)) return null;
    const rawStages = Array.isArray(payload.stages) ? payload.stages : null;
    const stages = rawStages ? rawStages.map((stage) => {
      if (!stage || typeof stage !== 'object') return null;
      const stageObj = stage as Record<string, unknown>;
      const players = Array.isArray(stageObj.players) ? stageObj.players.filter((id): id is string => typeof id === 'string') : [];
      const cumulativeBonus = Number(stageObj.cumulativeBonus);
      const resultingScore = Number(stageObj.resultingScore);
      if (!Number.isFinite(cumulativeBonus) || !Number.isFinite(resultingScore)) return null;
      return { players, cumulativeBonus, resultingScore };
    }).filter((stage): stage is { players: string[]; cumulativeBonus: number; resultingScore: number } => Boolean(stage)) : null;
    return { type: 'REAL_OVERTAKE', eventId: event.id, turn: event.turn, overtakerId, bonus, penalties, ...(stages && stages.length > 0 ? { stages } : {}) };
  }
  if (event.type === 'DICE_ROLL') {
    const playerId = typeof payload.playerId === 'string' ? payload.playerId : null;
    const keptMask = parseKeptMask(payload.keptMask);
    if (!playerId || !keptMask) return null;
    return { type: 'ROLL_INTENT', eventId: event.id, playerId, keptMask };
  }
  if (event.type === 'DICE_KEEP') {
    const playerId = typeof payload.playerId === 'string' ? payload.playerId : null;
    const keptMask = parseKeptMask(payload.keptMask);
    if (!playerId || !keptMask) return null;
    return { type: 'KEEP_INTENT', eventId: event.id, playerId, keptMask };
  }
  if (event.type === 'TURN_END') {
    const playerId = typeof payload.playerId === 'string' ? payload.playerId : null;
    if (!playerId) return null;
    return { type: 'END_TURN_INTENT', eventId: event.id, playerId };
  }
  if (event.type === 'GAME_CREATED') return { type: 'GAME_CREATED', eventId: event.id, ...(payload.mode === 'REAL' ? { mode: 'REAL' } : { mode: 'ONLINE' }) };
  if (event.type === 'ADD_PLAYER') {
    const playerId = typeof payload.playerId === 'string' ? payload.playerId : null;
    const name = typeof payload.name === 'string' ? payload.name.trim() : '';
    if (!playerId || !name) return null;
    const role = payload.role === 'viewer' ? 'viewer' : 'player';
    const ready = typeof payload.ready === 'boolean' ? payload.ready : undefined;
    const isBot = payload.isBot === true;
    const botProfile = payload.botProfile === 'conservative' || payload.botProfile === 'balanced' || payload.botProfile === 'risky' ? payload.botProfile : undefined;
    return { type: 'UPSERT_PLAYER', eventId: event.id, playerId, name, role, ...(typeof ready === 'boolean' ? { ready } : {}), ...(isBot ? { isBot: true } : {}), ...(botProfile ? { botProfile } : {}), ...(typeof payload.isHost === 'boolean' ? { isHost: payload.isHost } : {}) };
  }
  if (event.type === 'VIEWER_JOINED') {
    const viewerId = typeof payload.viewerId === 'string' ? payload.viewerId : null;
    const name = typeof payload.name === 'string' ? payload.name.trim() : '';
    if (!viewerId || !name) return null;
    return { type: 'UPSERT_PLAYER', eventId: event.id, playerId: viewerId, name, role: 'viewer', isHost: false };
  }
  if (event.type === 'PLAYER_READY') {
    const playerId = typeof payload.playerId === 'string' ? payload.playerId : null;
    const ready = payload.ready === true;
    if (!playerId) return null;
    return { type: 'SET_READY', eventId: event.id, playerId, ready };
  }
  if (event.type === 'NEXT_GAME_PARTICIPATION') {
    const playerId = typeof payload.playerId === 'string' ? payload.playerId : null;
    if (!playerId || typeof payload.participating !== 'boolean') return null;
    return {
      type: 'SET_NEXT_GAME_PARTICIPATION',
      eventId: event.id,
      playerId,
      participating: payload.participating,
    };
  }
  if (event.type === 'ORDER_FINALIZED') {
    const order = Array.isArray(payload.order) ? payload.order.filter((id): id is string => typeof id === 'string' && id.length > 0) : [];
    return { type: 'FINALIZE_ORDER', eventId: event.id, ...(order.length > 0 ? { order } : {}) };
  }
  if (event.type === 'GAME_STARTED') {
    const firstPlayerId = typeof payload.firstPlayerId === 'string' ? payload.firstPlayerId : null;
    return { type: 'START_GAME', eventId: event.id, ...(firstPlayerId ? { firstPlayerId } : {}) };
  }
  if (event.type === 'SETTINGS_CHANGED') {
    const hostId = typeof payload.hostId === 'string' ? payload.hostId : null;
    const raw = payload.settings as Record<string, unknown> | undefined;
    if (!hostId || !raw || typeof raw !== 'object') return null;
    const rapid = raw.rapid as RapidSettings | undefined;
    return {
      type: 'SET_SETTINGS',
      eventId: event.id,
      hostId,
      settings: {
        tunnels: raw.tunnels === true,
        rescueByFive: raw.rescueByFive !== false,
        zones: raw.zones === true,
        zonesAvoidTunnels: raw.zonesAvoidTunnels === true,
        ...(rapid ? { rapid } : {}),
      },
    };
  }
  if (event.type === 'HOST_TAKEOVER') {
    const newHostPlayerId = typeof payload.newHostPlayerId === 'string' ? payload.newHostPlayerId : null;
    if (!newHostPlayerId) return null;
    return { type: 'HOST_TAKEOVER', eventId: event.id, newHostPlayerId, reason: typeof payload.reason === 'string' && payload.reason.length > 0 ? payload.reason : 'UNKNOWN' };
  }
  if (event.type === 'PLAYER_LEFT') {
    const playerId = typeof payload.playerId === 'string' ? payload.playerId : null;
    if (!playerId) return null;
    return { type: 'PLAYER_LEFT', eventId: event.id, playerId, ...(typeof payload.reason === 'string' && payload.reason.length > 0 ? { reason: payload.reason } : {}) };
  }
  if (event.type === 'GAME_RESTARTED') {
    const classifiedRestart = classifyGameRestartedPayload(currentRoomId, payload);
    if (classifiedRestart.kind !== 'supported-same-room') return null;
    return {
      type: 'GAME_RESTARTED',
      eventId: event.id,
      newGameId: classifiedRestart.requestedRoomId,
      currentRoundId: classifiedRestart.currentRoundId,
    };
  }
  return null;
}

class DefaultOnlineGameBoundary implements OnlineGameBoundary {
  forGame(gameId: string, actor?: Partial<OnlineClientContext>): OnlineGameHandle {
    const connectEphemeral = async (client?: Partial<OnlineClientContext>) => {
      const transport = createOnlineTransport();
      return await transport.connect(gameId, {
        playerId: client?.playerId ?? null,
        playerName: client?.playerName ?? null,
        role: client?.role ?? null,
      });
    };

    const sendThroughEphemeralConnection = async (
      command: OnlineClientCommand,
      client?: Partial<OnlineClientContext>,
    ): Promise<boolean> => {
      const session = await connectEphemeral(resolveClientContext(command, client));
      if (!session) return false;
      try {
        const ready = await waitForSessionReady(session.onStatus.bind(session));
        if (!ready) {
          const snapshot: PartyKitCommandErrorSnapshot = {
            at: new Date().toISOString(), gameId, commandType: getCommandType(command), eventId: getCommandEventId(command), source: 'ephemeral', reason: 'SESSION_NOT_READY',
          };
          setLastPartyKitCommandError(snapshot);
          recordTransportTelemetry('PARTYKIT_EPHEMERAL_NOT_READY', snapshot, ['ack', 'timeout']);
          return false;
        }
        const ackPromise = waitForCommandAck(session, command, { gameId, source: 'ephemeral' });
        await session.send(command);
        const acked = await ackPromise;
        if (!acked) {
          const last = getLastPartyKitCommandError();
          recordTransportTelemetry('PARTYKIT_EPHEMERAL_ACK_FAILED', {
            gameId, commandType: getCommandType(command), eventId: getCommandEventId(command), reason: last?.reason ?? null, code: last?.code ?? null,
          }, ['ack']);
        }
        return acked;
      } finally {
        await session.disconnect();
      }
    };

    const sendIntentInternal = async (
      command: OnlineClientCommand,
      options?: { delivery?: CommandDelivery },
      clientOverride?: Partial<OnlineClientContext>,
    ): Promise<CommandResult> => {
      if (options?.delivery === 'ephemeral') {
        const success = await sendThroughEphemeralConnection(command, clientOverride ?? actor);
        const last = getLastPartyKitCommandError();
        return {
          success,
          eventId: getCommandEventId(command),
          error: success ? undefined : (last?.message ?? 'Command not acknowledged'),
          errorCode: success ? undefined : (last?.code ?? 'PARTYKIT_COMMAND_FAILED'),
        };
      }

      const existingSession = getOnlineTransportSession(gameId);
      if (existingSession) {
        const runtimeReady = await isRuntimeSessionReady(existingSession);
        if (runtimeReady) {
          try {
            const ackPromise = waitForCommandAck(existingSession, command, { gameId, source: 'runtime' });
            await existingSession.send(command);
            const acked = await ackPromise;
            if (acked) return { success: true, eventId: getCommandEventId(command) };
            const last = getLastPartyKitCommandError();
            recordTransportTelemetry('PARTYKIT_RUNTIME_ACK_FAILED', {
              gameId, commandType: getCommandType(command), eventId: getCommandEventId(command), reason: last?.reason ?? null, code: last?.code ?? null,
            }, ['ack']);
            if (last?.source === 'runtime' && last?.reason === 'ACK_TIMEOUT' && TURN_INTENT_COMMANDS.has(command.type)) {
              recordTransportTelemetry('PARTYKIT_RUNTIME_TIMEOUT_SKIP_EPHEMERAL_RETRY', {
                gameId, commandType: getCommandType(command), eventId: getCommandEventId(command), reason: last.reason,
              }, ['ack', 'timeout']);
              return { success: false, eventId: getCommandEventId(command), error: 'Command acknowledgement timed out', errorCode: 'ACK_TIMEOUT' };
            }
            if (last?.source === 'runtime' && last?.reason === 'SERVER_ERROR') {
              const shouldRetryViaEphemeral = typeof last.code === 'string' && RECOVERABLE_RUNTIME_AUTH_ERRORS.has(last.code);
              if (!shouldRetryViaEphemeral) {
                return { success: false, eventId: getCommandEventId(command), error: last.message ?? 'Server rejected command', errorCode: last.code ?? 'SERVER_ERROR', errorDetails: last.reason ?? undefined };
              }
            }
          } catch {
            const snapshot: PartyKitCommandErrorSnapshot = {
              at: new Date().toISOString(), gameId, commandType: getCommandType(command), eventId: getCommandEventId(command), source: 'runtime', reason: 'RUNTIME_SEND_THROW',
            };
            setLastPartyKitCommandError(snapshot);
            recordTransportTelemetry('PARTYKIT_RUNTIME_SEND_THROW', snapshot, ['error']);
          }
        } else {
          const payload = { gameId, commandType: getCommandType(command), eventId: getCommandEventId(command) };
          if (command.type === 'SET_DEV_BOT_BEHAVIOR') {
            if (isVerboseLoggingEnabled()) logger.debug('PARTYKIT_RUNTIME_NOT_READY_FALLBACK', payload);
            recordDebugTrace('transport:PARTYKIT_RUNTIME_NOT_READY_FALLBACK', payload, { source: 'onlineGameBoundary', tags: ['partykit', 'transport', 'ack', 'fallback', 'dev-only'] });
          } else {
            recordTransportTelemetry('PARTYKIT_RUNTIME_NOT_READY_FALLBACK', payload, ['ack', 'fallback']);
          }
        }
      }

      const success = await sendThroughEphemeralConnection(command, resolveClientContext(command, clientOverride ?? actor));
      const last = getLastPartyKitCommandError();
      if (!success) {
        recordTransportTelemetry('PARTYKIT_COMMAND_FAILED_FINAL', {
          gameId, commandType: getCommandType(command), eventId: getCommandEventId(command), finalSource: 'ephemeral', reason: last?.reason ?? null, code: last?.code ?? null,
        }, ['ack', 'error']);
      }
      return {
        success,
        eventId: getCommandEventId(command),
        error: success ? undefined : (last?.message ?? 'Command not acknowledged'),
        errorCode: success ? undefined : (last?.code ?? 'PARTYKIT_COMMAND_FAILED'),
        errorDetails: success ? undefined : (last?.reason ?? undefined),
      };
    };

    const requestLobbyStateInternal = async (): Promise<OnlineLobbyStateSnapshot | null> => {
      const existingSession = getOnlineTransportSession(gameId);
      if (existingSession) {
        try {
          const statePromise = waitForLobbyStateFromSession(existingSession);
          await existingSession.send({ type: 'REQUEST_STATE' });
          const state = await statePromise;
          if (state) return state;
          recordTransportTelemetry('PARTYKIT_RUNTIME_REQUEST_STATE_TIMEOUT_FALLBACK', { gameId, requestType: 'LOBBY_STATE' }, ['state', 'timeout']);
        } catch (error) {
          recordTransportTelemetry('PARTYKIT_RUNTIME_REQUEST_STATE_FAILED_FALLBACK', { gameId, requestType: 'LOBBY_STATE', error: getErrorMessage(error) }, ['state', 'error']);
        }
      }
      const session = await connectEphemeral(actor);
      if (!session) return null;
      try {
        const statePromise = waitForLobbyStateFromSession(session);
        const ready = await waitForSessionReady(session.onStatus.bind(session));
        if (ready) await session.send({ type: 'REQUEST_STATE' });
        return await statePromise;
      } finally {
        await session.disconnect();
      }
    };

    const requestStateSnapshotInternal = async (): Promise<OnlineSnapshot | null> => {
      const existingSession = getOnlineTransportSession(gameId);
      if (existingSession) {
        try {
          const snapshotPromise = waitForStateSnapshotFromSession(existingSession);
          await existingSession.send({ type: 'REQUEST_STATE' });
          const snapshot = await snapshotPromise;
          if (snapshot) return snapshot;
          recordTransportTelemetry('PARTYKIT_RUNTIME_REQUEST_STATE_TIMEOUT_FALLBACK', { gameId, requestType: 'SNAPSHOT' }, ['state', 'timeout']);
        } catch (error) {
          recordTransportTelemetry('PARTYKIT_RUNTIME_REQUEST_STATE_FAILED_FALLBACK', { gameId, requestType: 'SNAPSHOT', error: getErrorMessage(error) }, ['state', 'error']);
        }
      }
      const session = await connectEphemeral(actor);
      if (!session) return null;
      try {
        const snapshotPromise = waitForStateSnapshotFromSession(session);
        const ready = await waitForSessionReady(session.onStatus.bind(session));
        if (ready) await session.send({ type: 'REQUEST_STATE' });
        return await snapshotPromise;
      } finally {
        await session.disconnect();
      }
    };

    const requestSnapshotEventsInternal = async (sinceSequence = 0): Promise<EventWithSequence[]> => {
      const runSnapshotFetch = async (requestSinceSequence: number): Promise<{ events: GameEvent[]; completed: boolean; timedOut: boolean }> => {
        const session = await connectEphemeral(actor);
        if (!session) return { events: [], completed: false, timedOut: true };
        try {
          const ready = await waitForSessionReady(session.onStatus.bind(session));
          if (!ready) return { events: [], completed: false, timedOut: true };
          const eventsPromise = waitForSnapshotEventsFromSession(session, requestSinceSequence);
          await session.send({ type: 'REQUEST_SNAPSHOT', sinceSequence: requestSinceSequence });
          return await eventsPromise;
        } finally {
          await session.disconnect();
        }
      };

      const mergedById = new Map<string, GameEvent>();
      const normalizedSince = Number.isFinite(sinceSequence) ? Math.max(0, Math.floor(sinceSequence)) : 0;
      let requestSince = normalizedSince;
      let completed = false;
      for (let attempt = 0; attempt < SNAPSHOT_MAX_FETCH_ATTEMPTS; attempt += 1) {
        const attemptResult = await runSnapshotFetch(requestSince);
        for (const event of attemptResult.events) {
          const incomingSequence = typeof (event as { sequenceNumber?: unknown }).sequenceNumber === 'number' ? (event as { sequenceNumber: number }).sequenceNumber : null;
          const existing = mergedById.get(event.id);
          const existingSequence = existing && typeof (existing as { sequenceNumber?: unknown }).sequenceNumber === 'number' ? (existing as { sequenceNumber: number }).sequenceNumber : null;
          if (!existing || (incomingSequence ?? -1) >= (existingSequence ?? -1)) mergedById.set(event.id, event);
        }
        if (attemptResult.completed) {
          completed = true;
          break;
        }
        const maxReceivedSequence = attemptResult.events.reduce((max, event) => {
          const sequence = typeof (event as { sequenceNumber?: unknown }).sequenceNumber === 'number' ? (event as { sequenceNumber: number }).sequenceNumber : null;
          return sequence != null && sequence > max ? sequence : max;
        }, requestSince);
        if (maxReceivedSequence <= requestSince) break;
        requestSince = maxReceivedSequence;
      }
        let syntheticSequence = normalizedSince;
        const mergedEvents = Array.from(mergedById.values())
          .filter((event) => {
            const sequence = typeof (event as { sequenceNumber?: unknown }).sequenceNumber === 'number' ? (event as { sequenceNumber: number }).sequenceNumber : null;
            return sequence == null || sequence > normalizedSince;
          })
          .sort((left, right) => {
            const leftSequence = typeof (left as { sequenceNumber?: unknown }).sequenceNumber === 'number' ? (left as { sequenceNumber: number }).sequenceNumber : Number.MAX_SAFE_INTEGER;
            const rightSequence = typeof (right as { sequenceNumber?: unknown }).sequenceNumber === 'number' ? (right as { sequenceNumber: number }).sequenceNumber : Number.MAX_SAFE_INTEGER;
            return leftSequence - rightSequence;
          })
          .map((event) => {
            const existingSequence = typeof (event as { sequenceNumber?: unknown }).sequenceNumber === 'number'
              ? (event as { sequenceNumber: number }).sequenceNumber
              : null;
            if (existingSequence != null) {
              syntheticSequence = Math.max(syntheticSequence, existingSequence);
              return { ...event, sequenceNumber: existingSequence } as EventWithSequence;
            }
            syntheticSequence += 1;
            return { ...event, sequenceNumber: syntheticSequence } as EventWithSequence;
          });
      if (!completed) {
        recordTransportTelemetry('PARTYKIT_SNAPSHOT_EVENTS_INCOMPLETE', { gameId, sinceSequence: normalizedSince, mergedCount: mergedEvents.length, lastRequestSince: requestSince, maxAttempts: SNAPSHOT_MAX_FETCH_ATTEMPTS }, ['snapshot', 'timeout']);
      }
      return mergedEvents;
    };

    return {
      appendEvent: async (event: GameEvent) => {
        const normalizedEvent = normalizeEventForRpc(event);
        try {
          const command = buildCommandFromEvent(normalizedEvent, gameId);
          if (!command) {
            if (isVerboseLoggingEnabled()) {
              logger.warn('ONLINE_MIRROR_UNSUPPORTED_EVENT', { gameId, eventType: normalizedEvent.type, eventId: normalizedEvent.id });
            }
            return { success: false, acknowledgedEventId: undefined, error: 'Unsupported event mapping', errorCode: 'INVALID_COMMAND_MAPPING' } satisfies AppendEventResult;
          }
          const shouldPreferEphemeral = normalizedEvent.turn === 0 && (
            normalizedEvent.type === 'GAME_CREATED'
            || normalizedEvent.type === 'ADD_PLAYER'
            || normalizedEvent.type === 'VIEWER_JOINED'
            || normalizedEvent.type === 'SETTINGS_CHANGED'
          );
          const result = await sendIntentInternal(
            command,
            {
              delivery: shouldPreferEphemeral ? 'ephemeral' : 'auto',
            },
            actor ?? resolveClientFromEvent(normalizedEvent),
          );
          return {
            success: result.success,
            acknowledgedEventId: normalizedEvent.id,
            error: result.error,
            ...(result.errorCode ? { errorCode: result.errorCode } : {}),
            ...(result.errorDetails ? { errorDetails: result.errorDetails } : {}),
            ...(result.errorHint ? { errorHint: result.errorHint } : {}),
          } satisfies AppendEventResult;
        } catch (error) {
          if (isVerboseLoggingEnabled()) {
            logger.warn('ONLINE_MIRROR_SEND_FAILED', { gameId, eventType: normalizedEvent.type, eventId: normalizedEvent.id, message: getErrorMessage(error) });
          }
          return { success: false, acknowledgedEventId: normalizedEvent.id, error: getErrorMessage(error), errorCode: 'PARTYKIT_APPEND_FAILED' } satisfies AppendEventResult;
        }
      },
      sendIntent: (intent, options) => sendIntentInternal(intent, options, actor),
      read: {
        snapshot: requestStateSnapshotInternal,
        lobby: requestLobbyStateInternal,
        events: requestSnapshotEventsInternal,
      },
    };
  }
}

export const onlineGameBoundary: OnlineGameBoundary = new DefaultOnlineGameBoundary();
